import type {
	CellContext,
	ColumnDef,
	FilterFn,
	Row,
} from "@tanstack/react-table";
import type { AutoCombineEntryV2 } from "@/shared/cache";
import { columns as baseColumns, formatDate } from "./columns";
import { enumFilterFn } from "./filters/enumFilterFn";
import { RowTimerControl } from "./RowTimerControl";
import { RunningUnitsCell } from "./RunningUnitsCell";
import { formatTimekeeperName, type Timekeeper } from "./timekeepers";
import { makeColumnOrderParser, makeVisibilityParser } from "./viewParams";

/**
 * Trailing per-row smart-timer control. Deliberately kept out of
 * `AUTO_COLUMN_LABELS` so it never appears in the Columns menu (users can't
 * hide or reorder it), and non-hiding/sorting/resizing so it stays a fixed
 * action gutter. Rendered only in the entry-group (auto-combine) table.
 */
export const TIMER_COLUMN_ID = "timer";

const timerColumn: ColumnDef<AutoCombineEntryV2> = {
	id: TIMER_COLUMN_ID,
	enableGrouping: false,
	enableHiding: false,
	enableSorting: false,
	enableResizing: false,
	size: 52,
	minSize: 52,
	header: () => <span className="sr-only">Timer</span>,
	cell: ({ row }) => <RowTimerControl entry={row.original} />,
};

// Entry-group mode reuses the shared column definitions verbatim — an
// entry-group row carries display fields plus nested source entries, so the
// cells render identically.

export const AUTO_COLUMN_LABELS: Record<string, string> = {
	date: "Date",
	timekeeper: "Timekeeper",
	matter: "Matter",
	codes: "Codes",
	description: "Narrative",
	status: "Status",
	billableCategory: "Billing",
	units: "Units",
};

export const AUTO_DEFAULT_COLUMN_ORDER = Object.keys(AUTO_COLUMN_LABELS);

export const AUTO_DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = {
	application: false,
	// Hidden filter-only columns (shared with normal mode).
	client: false,
	task: false,
	activity: false,
	matterName: false,
	billable: false,
	unitsSeconds: false,
};

interface AutoColumnOptions {
	/** Include smart-timer controls and live units for the attorney entries page. */
	smartTimer?: boolean;
}

function formatTaskTime(time: string): string | null {
	const match = /^(\d{2}):(\d{2})/.exec(time);
	if (!match) return null;

	const hour = Number(match[1]);
	const minute = Number(match[2]);
	if (hour > 23 || minute > 59) return null;

	const period = hour >= 12 ? "PM" : "AM";
	const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
	return minute === 0
		? `${hour12} ${period}`
		: `${hour12}:${String(minute).padStart(2, "0")} ${period}`;
}

/** Display one task's time or the chronological span of a multi-task group. */
export function getEntryGroupTimeLabel(
	entry: AutoCombineEntryV2,
): string | null {
	const taskTimes = entry.miniTransactions
		.map((task) => {
			const label = task.time ? formatTaskTime(task.time) : null;
			if (!label) return null;
			return {
				label,
				sortKey: `${task.date || entry.date}T${task.time}`,
			};
		})
		.filter((task): task is { label: string; sortKey: string } => task !== null)
		.sort((a, b) => a.sortKey.localeCompare(b.sortKey));

	if (taskTimes.length === 0) return null;
	if (entry.miniTransactions.length === 1 || taskTimes.length === 1) {
		return taskTimes[0].label;
	}

	return `${taskTimes[0].label} to ${taskTimes[taskTimes.length - 1].label}`;
}

function AutoCombineDateCell({ row }: { row: Row<AutoCombineEntryV2> }) {
	const timeLabel = getEntryGroupTimeLabel(row.original);
	return (
		<div className="flex flex-col gap-0.5 whitespace-nowrap">
			<span className="tabular-nums text-foreground">
				{formatDate(row.original.date)}
			</span>
			{timeLabel && (
				<span className="text-xs tabular-nums text-muted-foreground">
					{timeLabel}
				</span>
			)}
		</div>
	);
}

function autoCombineDateSortKey(entry: AutoCombineEntryV2): number {
	if (entry.lastEntryAt) {
		const timestamp = Date.parse(entry.lastEntryAt);
		if (!Number.isNaN(timestamp)) return timestamp;
	}
	const fallback = Date.parse(`${entry.date}T00:00:00Z`);
	return Number.isNaN(fallback) ? 0 : fallback;
}

function autoTimekeeperColumn(
	timekeepers: readonly Timekeeper[],
): ColumnDef<AutoCombineEntryV2> {
	return {
		id: "timekeeper",
		accessorFn: (entry) => entry.timekeeperId ?? "",
		enableGrouping: false,
		enableSorting: false,
		filterFn: enumFilterFn as FilterFn<AutoCombineEntryV2>,
		size: 132,
		minSize: 112,
		header: () => (
			<span className="-ml-1 text-sm font-medium text-muted-foreground">
				Timekeeper
			</span>
		),
		cell: ({ getValue }) => {
			const id = getValue<string | null>();
			const member = timekeepers.find((timekeeper) => timekeeper.id === id);
			return (
				<span className="whitespace-nowrap text-foreground">
					{member ? formatTimekeeperName(member) : (id ?? "–")}
				</span>
			);
		},
	};
}

// Safe widening: every base cell reads only display fields carried by
// `AutoCombineEntryV2`. The date cell is swapped for a span-aware variant
// (see above); everything else renders verbatim.
export function getAutoColumns(
	timekeepers: readonly Timekeeper[] | null,
	{ smartTimer = true }: AutoColumnOptions = {},
): ColumnDef<AutoCombineEntryV2>[] {
	const mapped = (
		baseColumns as unknown as ColumnDef<AutoCombineEntryV2>[]
	).map((col) =>
		col.id === "date"
			? {
					...col,
					cell: AutoCombineDateCell,
					sortingFn: (
						a: Row<AutoCombineEntryV2>,
						b: Row<AutoCombineEntryV2>,
					) => {
						const timeOrder =
							autoCombineDateSortKey(a.original) -
							autoCombineDateSortKey(b.original);
						return timeOrder || a.original.id.localeCompare(b.original.id);
					},
				}
			: smartTimer && col.id === "units"
				? {
						...col,
						cell: ({
							row,
							getValue,
						}: CellContext<AutoCombineEntryV2, unknown>) => (
							<RunningUnitsCell
								entry={row.original}
								value={getValue<number>()}
							/>
						),
					}
				: col.id === "_select"
					? { ...col, size: 40 }
					: col,
	);

	if (!timekeepers) return smartTimer ? [...mapped, timerColumn] : mapped;

	const [selectColumn, ...rest] = mapped;
	const columns = [selectColumn, autoTimekeeperColumn(timekeepers), ...rest];
	return smartTimer ? [...columns, timerColumn] : columns;
}

export const autoColumns = getAutoColumns(null);

export const autoVisibilityParser = makeVisibilityParser(
	AUTO_DEFAULT_COLUMN_VISIBILITY,
);
export const autoColumnOrderParser = makeColumnOrderParser(
	AUTO_DEFAULT_COLUMN_ORDER,
);
